import { isFuture, parseISO } from 'date-fns';
import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import React, { VFC } from 'react';

import { useCreation } from '@/apis/aniftyBackend/creations/getCreation';
import { PrimarySaleItemData } from '@/apis/aniftyBackend/marketplace/interface';
import { MintAskStatus } from '@/apis/aniftyBackend/mintAsks/getMintAsk';
import { weiToEth } from '@/apis/blockchain/blockchainCommon';
import AuctionCreationCard from '@/components/AuctionCreationCard';
import SaleCreationCard from '@/components/SaleCreationCard';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import getImgixURL from '@/utils/getImgixURL';
import getThumbnailURL from '@/utils/getThumbnailURL';

import styles from './MarketplaceItem.module.scss';
import MarketplaceReservedItemOverlay from './MarketplaceReservedItemOverlay';

type Props = {
  saleItem: PrimarySaleItemData;
};

const MarketplaceItem: VFC<Props> = ({ saleItem }: Props) => {
  const { i18n } = useTranslation();

  const creatorName =
    i18n.language === 'ja' && saleItem.creatorProfile.displayNameJP
      ? saleItem.creatorProfile.displayNameJP
      : saleItem.creatorProfile.displayNameEN;

  const tokenName =
    i18n.language === 'ja' && saleItem.tokenNameJA
      ? saleItem.tokenNameJA
      : saleItem.tokenName;

  // Is this creation reserved and not released yet?
  const isReservedItem =
    !!saleItem.reservedAt && isFuture(parseISO(saleItem.reservedAt));

  const { data: creation } = useCreation({ creationID: saleItem.creationID });

  return (
    <div
      className={
        isReservedItem ? styles.reservedMarketplaceItem : styles.marketplaceItem
      }
    >
      {isReservedItem && (
        <MarketplaceReservedItemOverlay
          creationID={saleItem.creationID}
          reservedAt={parseISO(saleItem.reservedAt)}
          creator={{
            userName: saleItem.creatorProfile.userName,
            displayName: creatorName,
            profileImageURL: saleItem.creatorProfile.profilePicUrl,
          }}
        />
      )}

      {saleItem.listingType === 0 ? (
        <SaleCreationCard
          link={routes.creationDetail(saleItem.creationID.toString())}
          creationImageUrl={saleItem.imageURL}
          thumbnailUrl={getThumbnailURL(saleItem.imageURL, {
            width: saleItem.width,
            height: saleItem.height,
            cropStartX: saleItem.cropStartX,
            cropStartY: saleItem.cropStartY,
            cropSize: saleItem.cropSize,
          })}
          creationName={tokenName}
          creatorName={creatorName}
          creatorProfileImageUrl={getImgixURL(
            saleItem.creatorProfile.profilePicUrl || DEFAULT_PROFILE_IMAGE_PATH
          )}
          price={weiToEth(saleItem.price)}
          availableTokenAmount={saleItem.availableTokenAmount}
          totalTokenAmount={saleItem.totalTokenAmount}
        />
      ) : (
        <AuctionCreationCard
          link={routes.creationDetail(saleItem.creationID.toString())}
          creationImageUrl={saleItem.imageURL}
          thumbnailUrl={getThumbnailURL(saleItem.imageURL, {
            width: saleItem.width,
            height: saleItem.height,
            cropStartX: saleItem.cropStartX,
            cropStartY: saleItem.cropStartY,
            cropSize: saleItem.cropSize,
          })}
          creationName={tokenName}
          creatorName={creatorName}
          creatorProfileImageUrl={getImgixURL(
            saleItem.creatorProfile.profilePicUrl || DEFAULT_PROFILE_IMAGE_PATH
          )}
          reservedPrice={formatEther(saleItem.price)}
          endTime={saleItem.endTime ? saleItem.endTime : null}
          highestBidPrice={
            saleItem.highestBidPrice && formatEther(saleItem.highestBidPrice)
          }
          status={
            creation?.mintAsk ? creation.mintAsk.status : MintAskStatus.Ongoing
          }
        />
      )}
    </div>
  );
};

export default MarketplaceItem;
